// src/utils/imageUpload.js - Firebase Storage upload utilities
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../firebase/config';

/**
 * Convert data URL to Blob
 */
export const dataURLToBlob = (dataURL) => {
  const arr = dataURL.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
};

/**
 * Generate a unique filename with timestamp
 */
export const generateFileName = (originalName, prefix = 'image') => {
  const timestamp = Date.now();
  const randomString = Math.random().toString(36).substring(2, 8);
  const extension = originalName?.split('.').pop() || 'jpg';
  return `${prefix}_${timestamp}_${randomString}.${extension}`;
};

/**
 * Sanitize brand name for use in folder paths
 * @param {string} brandName - Brand name to sanitize
 * @param {string} brandId - Brand ID for uniqueness
 * @returns {string} - Sanitized folder name
 */
export const sanitizeBrandName = (brandName, brandId) => {
  if (!brandName) return brandId; // Fallback to ID if no name

  // Sanitize the brand name for file system compatibility
  const sanitized = brandName
    .trim()                           // Remove leading/trailing spaces
    .replace(/[^\w\s-]/g, '')        // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '_')            // Replace spaces with underscores
    .replace(/_+/g, '_')             // Replace multiple underscores with single
    .replace(/^_|_$/g, '')           // Remove leading/trailing underscores
    .substring(0, 50);               // Limit length to 50 characters

  // Combine sanitized name with short ID for uniqueness
  const shortId = brandId ? brandId.substring(0, 8) : 'unknown';

  return sanitized ? `${sanitized}_${shortId}` : shortId;
};

/**
 * Upload image to Firebase Storage
 * @param {string} dataURL - Base64 data URL
 * @param {string} folderPath - Storage folder path (e.g., 'brands/logos', 'brands/covers', 'promotions')
 * @param {string} fileName - Optional custom filename
 * @returns {Promise<string>} - Download URL
 */
export const uploadImageToStorage = async (dataURL, folderPath, fileName = null) => {
  try {
    // Check authentication first
    const { auth } = await import('../firebase/config');
    const currentUser = auth.currentUser;

    if (!currentUser) {
      throw new Error('User must be authenticated to upload images. Please sign in first.');
    }

    console.log(`🔐 User authenticated: ${currentUser.email || currentUser.uid}`);

    // Convert data URL to blob
    const blob = dataURLToBlob(dataURL);
    console.log(`📦 Blob created: ${blob.size} bytes, type: ${blob.type}`);

    // Generate filename if not provided
    const finalFileName = fileName || generateFileName('image', folderPath.replace('/', '_'));

    // Create storage reference with more detailed logging
    const storageRef = ref(storage, `${folderPath}/${finalFileName}`);
    console.log(`📁 Storage reference created: ${storageRef.fullPath}`);

    // Upload the blob with metadata
    console.log(`⬆️ Uploading image to ${folderPath}/${finalFileName}...`);

    const metadata = {
      contentType: blob.type,
      customMetadata: {
        uploadedBy: currentUser.uid,
        originalSize: blob.size.toString(),
        uploadedAt: new Date().toISOString()
      }
    };

    const uploadResult = await uploadBytes(storageRef, blob, metadata);
    console.log(`✅ Upload successful! Metadata:`, uploadResult.metadata);

    // Get download URL
    const downloadURL = await getDownloadURL(uploadResult.ref);
    console.log(`🔗 Download URL generated: ${downloadURL}`);

    return downloadURL;
  } catch (error) {
    console.error('❌ Firebase Storage upload error:', {
      message: error.message,
      code: error.code,
      name: error.name,
      stack: error.stack
    });

    // Provide more specific error messages
    if (error.code === 'storage/unauthorized') {
      throw new Error('❌ Upload failed: User not authorized. Please check Firebase Storage security rules.');
    } else if (error.code === 'storage/canceled') {
      throw new Error('❌ Upload failed: Upload was canceled.');
    } else if (error.code === 'storage/unknown') {
      throw new Error('❌ Upload failed: Unknown error occurred. Check your internet connection and try again.');
    } else if (error.message.includes('CORS')) {
      throw new Error('❌ Upload failed: CORS policy error. Firebase Storage needs to be configured for localhost access.');
    } else {
      throw new Error(`❌ Upload failed: ${error.message}`);
    }
  }
};

/**
 * Delete image from Firebase Storage
 * @param {string} downloadURL - The download URL of the image to delete
 */
export const deleteImageFromStorage = async (downloadURL) => {
  try {
    if (!downloadURL || !downloadURL.includes('firebase')) {
      console.log('Invalid or non-Firebase URL, skipping deletion');
      return;
    }

    // Extract the path from the download URL
    const baseUrl = 'https://firebasestorage.googleapis.com/v0/b/';
    const bucket = 'indobrand-1e09e.appspot.com'; // Your storage bucket

    if (downloadURL.includes(baseUrl + bucket)) {
      // Extract the file path from the URL
      const urlParts = downloadURL.split('/o/')[1];
      const filePath = decodeURIComponent(urlParts.split('?')[0]);

      // Create storage reference and delete
      const storageRef = ref(storage, filePath);
      await deleteObject(storageRef);
      console.log(`Image deleted successfully: ${filePath}`);
    }
  } catch (error) {
    console.error('Error deleting image from Firebase Storage:', error);
    // Don't throw error - deletion failures shouldn't block other operations
  }
};

/**
 * Upload brand logo image
 * @param {string} dataURL - Base64 data URL
 * @param {string} brandId - Brand ID for folder organization
 * @param {string} brandName - Brand name for readable folder name
 * @returns {Promise<string>} - Download URL
 */
export const uploadBrandLogo = async (dataURL, brandId, brandName = null) => {
  const folderName = sanitizeBrandName(brandName, brandId);
  return uploadImageToStorage(dataURL, `brands/${folderName}/logo`, `logo_${Date.now()}.jpg`);
};

/**
 * Upload brand cover image
 * @param {string} dataURL - Base64 data URL
 * @param {string} brandId - Brand ID for folder organization
 * @param {string} brandName - Brand name for readable folder name
 * @returns {Promise<string>} - Download URL
 */
export const uploadBrandCover = async (dataURL, brandId, brandName = null) => {
  const folderName = sanitizeBrandName(brandName, brandId);
  return uploadImageToStorage(dataURL, `brands/${folderName}/cover`, `cover_${Date.now()}.jpg`);
};

/**
 * Upload promotion image
 * @param {string} dataURL - Base64 data URL
 * @param {string} brandId - Brand ID for folder organization
 * @param {string} promotionId - Promotion ID for filename
 * @param {string} brandName - Brand name for readable folder name
 * @returns {Promise<string>} - Download URL
 */
export const uploadPromotionImage = async (dataURL, brandId, promotionId, brandName = null) => {
  const folderName = sanitizeBrandName(brandName, brandId);
  return uploadImageToStorage(
    dataURL,
    `brands/${folderName}/promotions`,
    `promotion_${promotionId}_${Date.now()}.jpg`
  );
};

/**
 * Compress image before upload (optional - for better performance)
 * @param {string} dataURL - Base64 data URL
 * @param {number} maxWidth - Maximum width
 * @param {number} maxHeight - Maximum height
 * @param {number} quality - JPEG quality (0-1)
 * @returns {Promise<string>} - Compressed data URL
 */
export const compressImageForUpload = async (dataURL, maxWidth = 1200, maxHeight = 1200, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Calculate new dimensions
      let { width, height } = img;
      if (width > maxWidth || height > maxHeight) {
        if (width > height) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        } else {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;

      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);
      const compressedDataURL = canvas.toDataURL('image/jpeg', quality);
      resolve(compressedDataURL);
    };
    img.onerror = reject;
    img.src = dataURL;
  });
};